import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMe } from '@/api/hooks'
import type { Section } from '@/api/types'

const STORAGE_KEY = 'academy-section'

interface SectionContextValue {
  /** The active section the browse views should show. */
  active: Section
  setActive: (section: Section) => void
  /** Sections the current user may access (from /me). */
  available: Section[]
}

const SectionContext = createContext<SectionContextValue | null>(null)

/**
 * Holds the active content section (customer vs internal). Defaults to
 * `customer` (the populated onboarding library); internal staff can switch.
 * Persists the choice, and keeps it within what /me says the user may access.
 */
export function SectionProvider({ children }: { children: ReactNode }) {
  const { data: me } = useMe()
  // Memoised so the fallback array keeps a stable identity across renders
  // (it feeds the effect/value deps below).
  const available = useMemo<Section[]>(() => me?.sections ?? ['customer'], [me])

  const [active, setActive] = useState<Section>(
    () => (localStorage.getItem(STORAGE_KEY) as Section | null) ?? 'customer',
  )

  // Once /me has loaded, if the persisted active section isn't allowed for this
  // user, fall back to customer. Guarded on `me` so a valid persisted choice
  // isn't clobbered during the initial load (when sections aren't known yet).
  useEffect(() => {
    if (me && !me.sections.includes(active)) setActive('customer')
  }, [me, active])

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, active)
  }, [active])

  const value = useMemo<SectionContextValue>(
    () => ({ active, setActive, available }),
    [active, available],
  )

  return <SectionContext.Provider value={value}>{children}</SectionContext.Provider>
}

// Provider + hook colocated by design; the fast-refresh rule doesn't apply here.
// eslint-disable-next-line react-refresh/only-export-components
export function useSection(): SectionContextValue {
  const ctx = useContext(SectionContext)
  if (!ctx) throw new Error('useSection must be used within SectionProvider')
  return ctx
}
